import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import useLocations from "../../data/useLocations";
import useLocationGroups from "../../data/useLocationGroups";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ILocation, LocationType } from "../../models/Location";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string,
  type: LocationType,
  capacity?: number | null,
  locationGroupId?: string | null,
  longitude: number,
  latitude: number,
};

interface LocationsModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  location?: ILocation;
}

export default function LocationsModal({ open, setOpen, location }: LocationsModalProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Locations");
  const { deleteHandlers } = useDefaultCRUDHandlers("Locations");
  const { update, create, remove } = useLocations();
  const { locationGroups: allLocationGroups } = useLocationGroups();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  const locationTypeOptions = [
    {
      id: LocationType.Yard,
      name: "Yard"
    },
    {
      id: LocationType.Racking,
      name: "Racking"
    }
  ];

  const locationGroupOptions = [
    { id: "", name: "None" },
    ...(allLocationGroups.data?.data ?? []).map((group) => ({
      id: group.id,
      name: group.name,
    }))
  ];

  const onSubmit = (data: any) => {

    if (data.longitude === "" || data.latitude === "") {
      data.longitude = null;
      data.latitude = null;
    }

    if (data.capacity === "") {
      data.capacity = null;
    }

    if (data.locationGroupId === "" || data.locationGroupId === undefined) {
      data.locationGroupId = null;
    }

    const updatedLocation = {
      ...location,
      ...data
    };

    if (location !== undefined) {
      update.mutate(updatedLocation, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      create.mutate(updatedLocation, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  }

  const handleDelete = () => {
    remove.mutate(location?.id!, {
      onSuccess: (data) => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  }

  useEffect(() => {

    if (location !== undefined) {
      reset({
        name: location.name,
        type: location.type,
        capacity: location.capacity ?? null,
        locationGroupId: location.locationGroupId ?? "",
        longitude: location.longitude,
        latitude: location.latitude
      });
    } else {
      reset({
        type: LocationType.Yard,
        locationGroupId: ""
      })
    }
  }, [location, open, reset]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={"Delete Location"} message={`Are you sure you want to delete this location?\n This action cannot be undone.`}
        confirmButtonText={"Delete"} onConfirm={handleDelete} isLoading={false}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{`${location ? "Edit" : "Add"} a Location`}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} />

              <SelectInput label="Type" registerName="type" register={register} watch={watch} setValue={setValue} options={locationTypeOptions} />

              <NumberInput label="Capacity" registerName="capacity" register={register} />

              <SelectInput label="Section" registerName="locationGroupId" register={register} watch={watch} setValue={setValue} options={locationGroupOptions} />

              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <TextInput label="Longitude" registerName="longitude" register={register} />
                <TextInput label="Latitude" registerName="latitude" register={register} />
              </div>

              <ButtonInput label={`${location ? "Save Changes" : "Create"}`} isSubmit={true} onClick={undefined} classes={undefined} />
              {location !== undefined && (
                <ButtonInput label={"Delete"} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes={"!bg-red-500 hover:!bg-red-600"} />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
